/**
 * Cash Casino — the slot cabinet. PHASE D: the real machine. The session is
 * `./serverSession` (POST /api/casino/spin — one atomic per-user transaction),
 * imported under the old name so everything below is unchanged; `?practice=1`
 * still hands back the Phase-A local session for the dev sandbox.
 * Solo game (home vs one user): no table clock, no crowd.
 *
 * Pick a chip → SPIN (or AUTO ×10). Three reels stop left to right with an
 * overshoot; winning paylines blink; the plate drops for BIG WIN / JACKPOT.
 * Every X shown is TOTAL RETURN on the whole stake (paytable = the reachable one).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { press } from './press';
import { CasinoMenu } from './Menu';
import { type Symbol as Sym, REELS, PAYLINES, LINE_X, TWO_SEVENS_X, SCATTER_X, fmtX } from './engine';
import { ServerSpinSession as LocalSpinSession, type SpinSession, type SpinResult, type Wallet } from './serverSession';
import { SYMBOL_SVG, BACKDROP_SVG, CABINET_SVG, LIP_SVG, CHIP_SVG } from './art';
import {
  initCsSound, isCsSoundOn, setCsSoundOn, unlockCsSound, initCsMusic, isCsMusicOn, setCsMusicOn,
  startCsMusic, stopCsMusic, bindCsVisibility, csBedPhase,
  sSpinStart, sReelTick, sReelStop, sNearMiss, sLineWin, sScatter, sWin, sBigWin, sJackpot, sNoWin, sCoin, sChip, sTap, sDenied, sAutoOn, sAutoOff,
} from './sound';
import './casino.css';

/* `press` moved to ./press so Menu.tsx can share the same `armed` map — two copies would mean
   a pointer armed in one file could never be matched in the other. */

// Opening balance BEFORE the server has spoken. It used to be 10,000 — the Phase-A practice
// bankroll — which meant a real-money cabinet showed the player ₹10,000 they did not have
// until the first `/state` landed, and every affordability check (the chip rail, AUTO's
// "can I afford the next one") was made against that fiction. Zero plus the `walletReady`
// gate below means nothing is ever shown or staked against a number we invented.
// (2026-09-06; the practice session funds itself, so `?practice=1` is unaffected.)
const PRACTICE_START = 0;
const CHIPS = [10, 50, 100, 500, 1000, 5000];
const AUTO_SPINS = 10;
const COPIES = 3;                                      // the strip is repeated so a spin can travel ≥ one full loop
const DUR = [1100, 1500, 1900];                        // reel stop times, ms
const inr = (n: number) => `₹${n.toLocaleString('en-IN')}`;
const lsGet = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private */ } };
const NAME: Record<Sym, string> = { seven: 'SEVENS', bell: 'BELLS', diamond: 'DIAMONDS', bar: 'BEERS', cherry: 'CHERRIES', ball: 'BALLS', blank: '' };

/** the seven badges become <symbol>s once; every cell is a tiny <use> */
type ArtKey = keyof typeof SYMBOL_SVG;
const SYMBOL_DEFS = (Object.keys(SYMBOL_SVG) as ArtKey[]).map((k) => SYMBOL_SVG[k].replace(/^\s*<svg[^>]*>/, `<symbol id="cs-sym-${k}" viewBox="0 0 100 100">`).replace(/<\/svg>\s*$/, '</symbol>')).join('');
const useSym = (k: ArtKey) => `<svg viewBox="0 0 100 100" aria-hidden><use href="#cs-sym-${k}"/></svg>`;

const PAY_ROWS: { key: string; syms: ArtKey[]; x: number; label: string }[] = [
  { key: 'seven', syms: ['seven'], x: LINE_X.seven, label: '×3 LINE' },
  { key: 'bell', syms: ['bell'], x: LINE_X.bell, label: '×3 LINE' },
  { key: 'diamond', syms: ['diamond'], x: LINE_X.diamond, label: '×3 LINE' },
  { key: 'bar', syms: ['bar'], x: LINE_X.bar, label: '×3 LINE' },
  { key: 'cherry', syms: ['cherry'], x: LINE_X.cherry, label: '×3 LINE' },
  { key: 'two-sevens', syms: ['seven7'], x: TWO_SEVENS_X, label: '×2 LINE' },
  { key: 'scatter', syms: ['ball'], x: SCATTER_X, label: '×3 ANY' },
];
const LINE_PTS = PAYLINES.map((pl) => pl.map((row, r) => `${r * 100 + 50},${row * 100 + 50}`).join(' '));

type Plate = { big: string; small: string; cls: string } | null;

const CsPage = () => {
  const appRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const balRef = useRef<HTMLSpanElement>(null);
  const reelRefs = useRef<(HTMLDivElement | null)[]>([null, null, null]);
  const stripRefs = useRef<(HTMLDivElement | null)[]>([null, null, null]);

  const balanceRef = useRef(PRACTICE_START);
  const [balance, setBalance] = useState(PRACTICE_START);
  const [balFx, setBalFx] = useState('');
  const wallet = useMemo<Wallet>(() => ({
    balance: () => balanceRef.current,
    debit: (n) => { if (balanceRef.current < n) return false; balanceRef.current -= n; setBalance(balanceRef.current); setBalFx('dip'); window.setTimeout(() => setBalFx(''), 300); return true; },
    credit: (n) => { balanceRef.current += n; setBalance(balanceRef.current); },
  }), []);
  const sessionRef = useRef<SpinSession | null>(null);
  // Re-read on every render: the session flips this the moment its first `/state` (or a spin
  // ack) lands. `tick` exists because that flip can happen with NO balance movement — a player
  // whose real balance is exactly the seeded 0 — and the Wallet seam alone would not re-render,
  // so the cabinet would sit on `₹—` with the chips greyed for someone who is merely broke.
  const [, setTick] = useState(0);
  const walletReady = sessionRef.current?.walletReady() ?? false;

  const [chip, setChip] = useState(() => { const v = Number(lsGet('cs.chip')); return CHIPS.includes(v) ? v : 100; });
  const [soundOn, setSoundOn] = useState(initCsSound);
  const [musicOn, setMusicOn] = useState(initCsMusic);
  const [phase, setPhase] = useState<'idle' | 'spinning' | 'settling'>('idle');
  const phaseRef = useRef<'idle' | 'spinning' | 'settling'>('idle');
  const [auto, setAuto] = useState(0);
  const autoRef = useRef(0);
  const [lit, setLit] = useState<number[]>([]);
  const [hold, setHold] = useState<number[]>([]);
  const [hits, setHits] = useState<Set<string>>(new Set());
  const [scat, setScat] = useState<Set<string>>(new Set());
  const [litPay, setLitPay] = useState<Set<string>>(new Set());
  const [plate, setPlate] = useState<Plate>(null);
  const [plateOut, setPlateOut] = useState(false);
  const [marquee, setMarquee] = useState('chase');
  const [shaking, setShaking] = useState(false);
  const [strip, setStrip] = useState<{ text: string; cls: string }>({ text: 'PICK A CHIP · TAP SPIN', cls: 'pick' });
  const [lastWin, setLastWin] = useState(0);
  const [sessionNet, setSessionNet] = useState(0);
  const [hist, setHist] = useState<SpinResult[]>([]);
  const [coins, setCoins] = useState<{ id: number; n: number; cx: number; cy: number }[]>([]);
  const navigate = useNavigate();
  const [rulesOpen, setRulesOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [burst, setBurst] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hint, setHint] = useState(() => lsGet('cs.seen') !== '1');

  const curIdx = useRef<number[]>([0, 0, 0]);
  const timersRef = useRef<number[]>([]);
  const later = useCallback((fn: () => void, ms: number) => { timersRef.current.push(window.setTimeout(fn, ms)); }, []);
  const setPh = (p: 'idle' | 'spinning' | 'settling') => { phaseRef.current = p; setPhase(p); };

  useLayoutEffect(() => {
    const el = appRef.current, sg = stageRef.current; if (!el || !sg) return;
    const write = (w: number, h: number) => { el.style.setProperty('--cs-w', `${Math.round(w)}px`); el.style.setProperty('--cs-h', `${Math.round(h)}px`); };
    const cs = getComputedStyle(el);
    write(el.clientWidth, el.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom));
    sg.style.setProperty('--cs-sh', `${sg.clientHeight}px`);
    const ro = new ResizeObserver((es) => { for (const e of es) { if (e.target === el) write(e.contentRect.width, e.contentRect.height); else sg.style.setProperty('--cs-sh', `${Math.round(e.contentRect.height)}px`); } });
    ro.observe(el, { box: 'content-box' }); ro.observe(sg, { box: 'content-box' });
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let seed = Number(lsGet('cs_seed'));
    if (!seed) { seed = (Math.random() * 0xffffffff) >>> 0; lsSet('cs_seed', String(seed)); }
    const session = new LocalSpinSession(seed ^ (Date.now() & 0xffff), wallet);
    sessionRef.current = session;
    const offSession = session.subscribe?.(() => setTick((t) => t + 1)) ?? null;
    /* park each reel on a random stop so the cabinet never boots on the same picture */
    curIdx.current = [3, 17, 9];
    stripRefs.current.forEach((s, i) => { if (s) s.style.transform = `translateY(${-curIdx.current[i] * cellPx(i)}px)`; });
    if (isCsMusicOn() && isCsSoundOn()) startCsMusic();
    csBedPhase('idle');
    const unbind = bindCsVisibility();
    return () => { offSession?.(); sessionRef.current?.destroy(); unbind(); stopCsMusic(); timersRef.current.forEach(window.clearTimeout); };
  }, [wallet]);

  /* THE CELL'S HEIGHT, WHICH IS NOT ITS WIDTH (fixed 2026-09-06). This read `clientWidth` and got
     away with it only while cells were square. The reference cell is WIDER than tall (~1.25:1), and
     the moment that landed every strip offset was multiplied by the wrong number: symbols sat
     between rows and the reels looked broken. The reel box is exactly three cells tall, so its
     height over three is the cell pitch — and it stays correct whatever the aspect becomes. */
  const cellPx = (i: number) => { const el = reelRefs.current[i]; return el ? el.clientHeight / 3 : 80; };

  const fireCoins = (n = 8) => {
    const app = appRef.current, bal = balRef.current, win = windowRef.current;
    if (!app || !bal || !win) return;
    const ar = app.getBoundingClientRect(), br = bal.getBoundingClientRect(), xr = win.getBoundingClientRect();
    app.style.setProperty('--cs-wx', `${(br.left - ar.left + br.width / 2).toFixed(0)}px`);
    app.style.setProperty('--cs-wy', `${(br.top - ar.top + br.height / 2).toFixed(0)}px`);
    const id = Date.now();
    setCoins((c) => [...c, { id, n, cx: xr.left - ar.left + xr.width / 2, cy: xr.top - ar.top + xr.height / 2 }]);
    /* The stagger SHRINKS as the shower grows: a flat 45ms put the 26th jackpot coin 1125ms behind
       the first, which is both too slow to read as one burst and past the cleanup below. */
    const step = Math.min(45, 700 / n);
    for (let i = 0; i < Math.min(n, 12); i++) sCoin(i * step);
    /* cleanup must outlast the LAST coin's flight, not a fixed 1200ms — at 26 coins that was
       deleting the tail of the shower mid-air */
    later(() => setCoins((c) => c.filter((x) => x.id !== id)), n * step + 1100);
  };

  /* ── the spin ──────────────────────────────────────────────────────── */
  const stopAuto = () => { if (autoRef.current) { autoRef.current = 0; setAuto(0); sAutoOff(); } };
  const doSpin = async () => {
    const s = sessionRef.current; if (!s || phaseRef.current !== 'idle') return;
    unlockCsSound();
    // Nothing may be staked against a balance the server has not confirmed (2026-09-06).
    if (!s.walletReady()) { setStrip({ text: 'LOADING YOUR BALANCE…', cls: 'err' }); sDenied(); stopAuto(); return; }
    // Claim the machine BEFORE the await: `spin()` is a round trip on the real cabinet, and
    // this is what stops a second tap (or an AUTO tick) starting a second one inside it. The
    // reels do not move yet — only the dock closes and the button reads SPINNING.
    setPh('spinning');
    const res = await s.spin(chip);
    if (res.ok === false) {
      // The reels never turned, so nothing to unwind. `balance` gets the plain money line;
      // anything else prints the server's own sentence (bet limits, the daily loss cap,
      // maintenance) rather than a guess the player cannot act on.
      setPh('idle');
      const msg = res.reason === 'balance'
        ? 'NOT ENOUGH BALANCE · TAP ₹ TO ADD'
        : (s.lastError?.() || 'PLEASE WAIT…').toUpperCase();
      setStrip({ text: msg, cls: 'err' }); sDenied(); stopAuto(); return;
    }
    const r = res.result;
    setLit([]); setHold([]); setHits(new Set()); setScat(new Set()); setLitPay(new Set()); setPlate(null); setPlateOut(false);
    setMarquee('chase fast'); setStrip({ text: autoRef.current ? `AUTO · ${autoRef.current} LEFT · SPINNING…` : 'SPINNING…', cls: 'deal' });
    sSpinStart(); csBedPhase('spinning', 0); navigator.vibrate?.(10);
    if (hint) { setHint(false); lsSet('cs.seen', '1'); }
    /* a near miss: reels 0 and 1 carry sevens on a common payline */
    const nearMiss = PAYLINES.some((pl) => r.grid[0][pl[0]] === 'seven' && r.grid[1][pl[1]] === 'seven');
    for (let i = 0; i < 3; i++) {
      const el = stripRefs.current[i]; if (!el) continue;
      const cell = cellPx(i);
      const cur = curIdx.current[i], target = r.stops[i];
      let to = target + 32;                                          // one full loop, then land
      if (to - cur < 24 && target + 66 < COPIES * 32) to += 32;      // never a lazy short spin
      el.classList.remove('spin'); el.style.transform = `translateY(${-cur * cell}px)`;
      void el.getBoundingClientRect();
      el.style.setProperty('--cs-dur', `${DUR[i]}ms`); el.classList.add('spin');
      el.style.transform = `translateY(${-to * cell}px)`;
      const tick = window.setInterval(() => sReelTick(i), 60); timersRef.current.push(tick as unknown as number);
      later(() => {
        window.clearInterval(tick);
        el.classList.remove('spin'); el.style.transform = `translateY(${-target * cell}px)`;
        curIdx.current[i] = target;
        sReelStop(i); csBedPhase('spinning', (i + 1) / 3);
        if (i === 1 && nearMiss) { sNearMiss(); setMarquee('chase fast'); }
        if (i === 2) resolve(r);
      }, DUR[i] + 30);
    }
  };

  const resolve = (r: SpinResult) => {
    const s = sessionRef.current; if (!s) return;
    setPh('settling');
    const ev = r.ev;
    const n = ev.lines.length + (ev.scatter ? 1 : 0);
    if (n === 0) {
      s.settle(r.id);
      setHist((h) => [r, ...h].slice(0, 200));
      setSessionNet((v) => v - r.stake);
      sNoWin(); setShaking(true); later(() => setShaking(false), 320); setMarquee('chase');
      setStrip({ text: autoRef.current > 1 ? `NO WIN · AUTO ${autoRef.current - 1} LEFT` : 'NO WIN · SPIN AGAIN', cls: 'out' });
      csBedPhase('idle');
      later(() => finish(), 450);
      return;
    }
    ev.lines.forEach((l, k) => {
      later(() => {
        setLit((v) => [...v, l.line]);
        const pl = PAYLINES[l.line];
        setHits((h) => { const x = new Set(h); pl.forEach((row, reel) => x.add(`${reel}-${row}`)); return x; });
        setLitPay((p) => { const x = new Set(p); x.add(l.symbol === 'two-sevens' ? 'two-sevens' : l.symbol); return x; });
        sLineWin(l.line, l.x);
      }, 120 + k * 150);
    });
    if (ev.scatter) later(() => {
      setScat(new Set(r.grid.flatMap((col, reel) => col.map((sym, row) => (sym === 'ball' ? `${reel}-${row}` : ''))).filter(Boolean)));
      setLitPay((p) => { const x = new Set(p); x.add('scatter'); return x; });
      sScatter();
    }, 120 + ev.lines.length * 150);
    later(() => {
      const paid = s.settle(r.id);
      setHist((h) => [r, ...h].slice(0, 200));
      setLastWin(paid); setSessionNet((v) => v + paid - r.stake);
      setBalFx('up'); later(() => setBalFx(''), 900);
      setHold(ev.lines.map((l) => l.line));
      const jackpot = ev.lines.some((l) => l.symbol === 'seven');
      const bestName = ev.lines.length ? NAME[(ev.lines.slice().sort((a, b) => b.x - a.x)[0].symbol === 'two-sevens' ? 'seven' : ev.lines.slice().sort((a, b) => b.x - a.x)[0].symbol) as Sym] : 'SCATTER';
      /* EVERY WIN IS CELEBRATED, and the size of the celebration follows the size of the win.
         A small win used to get no plate at all — just a line of text and eight coins — so the
         most common good thing that happens in this game was also the least acknowledged. Three
         tiers now: WIN pops a mustard plate with a shine sweep; BIG WIN adds a rotating ray burst
         behind it; JACKPOT runs the burst faster and throbs the plate. Coins scale 8 / 16 / 26. */
      fireCoins(jackpot ? 26 : ev.totalX >= 15 ? 16 : 8);
      if (jackpot) { setPlate({ big: 'JACKPOT 7·7·7', small: `${inr(paid)} · ${fmtX(ev.totalX)}`, cls: 'jackpot' }); setBurst('jackpot'); sJackpot(); csBedPhase('bigwin'); setMarquee('lit'); }
      else if (ev.totalX >= 15) { setPlate({ big: 'BIG WIN', small: `${inr(paid)} · ${fmtX(ev.totalX)}`, cls: 'big' }); setBurst('big'); sBigWin(); csBedPhase('bigwin'); setMarquee('lit'); }
      else { setPlate({ big: `WIN ${inr(paid)}`, small: fmtX(ev.totalX), cls: 'win' }); sWin(paid); csBedPhase('win'); setMarquee('chase fast'); }
      setStrip({ text: `WIN ${inr(paid)} · ${fmtX(ev.totalX)} · ${ev.lines.length > 1 ? `${ev.lines.length} LINES` : bestName}`, cls: 'win' });
      const holdMs = jackpot ? 3600 : ev.totalX >= 15 ? 2400 : 1400;
      later(() => setPlateOut(true), holdMs - 200);
      later(() => { setPlate(null); setBurst(''); setMarquee('chase'); csBedPhase('idle'); finish(); }, holdMs);
    }, 120 + n * 150 + 250);
  };

  const finish = () => {
    setPh('idle');
    if (autoRef.current > 0) {
      autoRef.current -= 1; setAuto(autoRef.current);
      if (autoRef.current > 0) { if (balanceRef.current >= chipRef.current) later(() => { void doSpinRef.current(); }, 650); else { stopAuto(); setStrip({ text: 'AUTO STOPPED · NOT ENOUGH BALANCE', cls: 'err' }); } }
      else { sAutoOff(); setStrip((t) => ({ ...t, text: `${t.text} · AUTO DONE` })); }
    }
  };
  const chipRef = useRef(chip); chipRef.current = chip;
  const doSpinRef = useRef(doSpin); doSpinRef.current = doSpin;

  const toggleAuto = () => {
    unlockCsSound();
    if (autoRef.current > 0) { stopAuto(); setStrip({ text: 'AUTO STOPPED', cls: 'out' }); return; }
    if (!sessionRef.current?.walletReady()) { setStrip({ text: 'LOADING YOUR BALANCE…', cls: 'err' }); sDenied(); return; }
    autoRef.current = AUTO_SPINS; setAuto(AUTO_SPINS); sAutoOn();
    if (phaseRef.current === 'idle') void doSpin();
  };
  const onSpinPress = () => { if (autoRef.current > 0) { stopAuto(); setStrip({ text: 'AUTO STOPPED', cls: 'out' }); return; } void doSpin(); };

  /* ── render ────────────────────────────────────────────────────────── */
  const busy = phase !== 'idle';
  // Until the server has told us the balance there is nothing true to say about the machine,
  // so the strip says exactly that instead of inviting a tap that will be refused.
  const stripText = walletReady ? strip.text : 'LOADING YOUR BALANCE…';
  const stripCls = walletReady ? strip.cls : 'pick';
  const stripKey = stripText.replace(/\d+/g, '#');
  const dotOf = (r: SpinResult) => (r.ev.totalX === 0 ? { cls: 'none', text: '—' } : r.ev.totalX >= 15 ? { cls: 'big', text: fmtX(r.ev.totalX).replace('.00', '') } : { cls: 'win', text: fmtX(r.ev.totalX).replace('.00', '') });

  return (
    <div className="cs-root fixed inset-0 z-[9999]">
    <div ref={appRef} className="cs-app" onPointerDown={unlockCsSound}>
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden dangerouslySetInnerHTML={{ __html: `<defs>${SYMBOL_DEFS}</defs>` }} />

      {/* the atomic wallpaper — drawn once, behind every zone */}
      <div className="cs-paper" aria-hidden dangerouslySetInnerHTML={{ __html: BACKDROP_SVG }} />

      {/* TOP BAR — Thimbles' bar, copied structure for structure: same wrappers, same 10/12 button
          sizes, same wallet chip, same safe-area padding. The ONLY change is the palette, gold
          instead of magenta. My previous attempt rebuilt it from the game's own CSS variables and
          came out 84px tall against Thimbles' ~64, which is why it read as a slab rather than a bar. */}
      <div
        /* THE BAR RESERVES THE NOTCH, and it is the only thing that does — see casino.css, where
           `.cs-app` used to pad by the same inset. Two places reserving it counted it twice:
           invisible on a desktop where it resolves to 0, ~82px doubled on a phone, which was the
           dead gold band above these buttons. Reserving it HERE rather than on the app is also what
           fills the notch with the bar's own gold instead of leaving a cream strip above it —
           exactly what Thimbles does. roulette.css:1676 documents the same trap. */
        className="flex justify-between items-center gap-2 px-3 sm:px-4 pb-3 sm:pb-4 bg-gradient-to-b from-[#7A5510] to-[#2E2106] shadow-md z-[150] w-full border-b border-amber-400/20 shrink-0"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
      >
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button" aria-label="Back" {...press(() => navigate('/games'))}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-black/30 border border-white/15 active:scale-95 transition text-white"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden><path d="M15.7 4.3 7 13l8.7 8.7 1.4-1.4L9.8 13l7.3-7.3z" /></svg>
          </button>
          <button
            type="button" aria-label="Settings" {...press(() => setSettingsOpen((o) => !o))}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-[0_0_5px_rgba(0,0,0,1)] active:scale-95 transition-transform"
          >
            <img src="/games/casino/Settings.png" className="w-full h-full object-contain" alt="Settings" />
          </button>
        </div>

        <div className="bg-gradient-to-b from-[#6A4A0E] to-[#241A05] rounded-lg shrink-0 flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-amber-400/25 shadow-[0_0_30px_rgba(246,168,35,0.08)] relative">
          <img src="/games/casino/Coin.png" className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10" alt="Coin" />
          <span ref={balRef} className={`cs-bal cs-num ${balFx} text-white font-bold text-base sm:text-lg mr-2 sm:mr-3 tracking-wide tabular-nums`}
                title={walletReady ? 'Your balance' : 'Loading your balance'}>{walletReady ? inr(balance) : '₹—'}</span>
          <button type="button" aria-label="Recharge wallet" {...press(() => navigate('/wallet'))}
                  className="rounded-sm flex items-center justify-center active:scale-95 transition-transform">
            <img src="/games/casino/Plus.png" className="w-6 h-6 sm:w-7 sm:h-7 object-contain" alt="Add" />
          </button>
        </div>
      </div>

      {/* THE STATUS LINE LIVES HERE, over the machine — not in the deck. Sitting above the
          chips it pushed the whole control block down and away from the paytable band; the
          reference has the chips tight under that band. */}
      <div className={`cs-strip ${stripCls}`} key={stripKey} {...press(() => setRulesOpen(true))}>{stripText}</div>

      <div ref={stageRef} className="cs-stage">
        <div ref={windowRef} className={`cs-abs cs-window ${shaking ? 'shake' : ''}`}>
          {/* the housing is DRAWN, because a bowed edge is not something border-radius can make */}
          <div className="cs-cab" aria-hidden dangerouslySetInnerHTML={{ __html: CABINET_SVG }} />
          <div className="cs-reels">
            {[0, 1, 2].map((i) => (
              <div key={i} ref={(el) => { reelRefs.current[i] = el; }} className="cs-reel">
                <div ref={(el) => { stripRefs.current[i] = el; }} className="cs-reelstrip">
                  {Array.from({ length: COPIES }, (_, c) => REELS[i].map((sym, j) => {
                    const row = c * 32 + j - curIdx.current[i];
                    const key = row >= 0 && row < 3 ? `${i}-${row}` : '';
                    return <div key={`${c}-${j}`} className={`cs-cell ${sym} ${key && hits.has(key) ? 'hit' : ''} ${key && scat.has(key) ? 'scat' : ''}`} dangerouslySetInnerHTML={{ __html: useSym(sym) }} />;
                  }))}
                </div>
              </div>
            ))}
          </div>
          <div className="cs-glass" />
          <div className="cs-lip" aria-hidden dangerouslySetInnerHTML={{ __html: LIP_SVG }} />
          <div className="cs-lines">
            <svg viewBox="0 0 300 300" preserveAspectRatio="none" aria-hidden>
              {LINE_PTS.map((pts, i) => <polyline key={i} className={`cs-line cs-line-${i} ${lit.includes(i) ? 'lit' : ''} ${hold.includes(i) ? 'hold' : ''}`} points={pts} />)}
            </svg>
          </div>
          <div className="cs-bezel" />
        </div>

        {/* the numbered payline studs down both cheeks, lit with their line */}
        {/* PAYLINE STUDS — 1·2·3 down the left cheek, 4 and 5 on the right with its middle slot
            deliberately empty. They used to read 1·2·5 on the left and 1·3·4 on the right, which
            both duplicated line 1 and put the numbers out of order. `null` renders an invisible
            stud rather than nothing at all, so the column still spaces evenly and 4 and 5 line up
            with 1 and 3 across the machine. */}
        <div className="cs-studs l" aria-hidden>
          {[0, 1, 2].map((i) => <span key={i} className={`cs-stud cs-num ${lit.includes(i) ? 'lit' : ''}`}>{i + 1}</span>)}
        </div>
        <div className="cs-studs r" aria-hidden>
          {[3, null, 4].map((i, n) => (i === null
            ? <span key={`gap${n}`} className="cs-stud blank" />
            : <span key={i} className={`cs-stud cs-num ${lit.includes(i) ? 'lit' : ''}`}>{i + 1}</span>))}
        </div>

        {plate && <div className={`cs-abs cs-plate ${plate.cls} ${plateOut ? 'out' : ''}`}><b>{plate.big}</b><small className="cs-num">{plate.small}</small></div>}
      </div>

      {/* THE PAYTABLE BAND — tan, and deliberately NOT part of the yellow controls. In the
          reference the cards belong to the machine: they say what the reels pay. The chips and the
          buttons are a different job on a different ground. Build 2 fused them into one gold deck
          and that is what made it stop looking like the reference. */}
      <div className="cs-payband">
        <div className="cs-pay">
          {PAY_ROWS.map((p) => (
            <div key={p.key} className={`${p.key === 'two-sevens' ? 'alt' : ''} ${litPay.has(p.key) ? 'lit' : ''}`}>
              <span dangerouslySetInnerHTML={{ __html: p.syms.map(useSym).join('') }} />
              <b className="cs-num">{fmtX(p.x).replace('.00', '')}</b>
              <small>{p.label}</small>
            </div>
          ))}
        </div>
      </div>

      {/* THE CONTROLS — golden yellow, chips and buttons only. */}
      <div className="cs-deck">

        <div className="cs-dock">
        <div className={`cs-rail ${busy ? 'closed' : ''}`}>
          {CHIPS.map((c) => (
            <button key={c} type="button" data-v={c} className={`cs-chip ${chip === c ? 'sel' : ''}`} disabled={!walletReady || c > balance || busy} aria-label={`₹${c} stake`}
                    {...press(() => { setChip(c); lsSet('cs.chip', String(c)); sTap(); })}>
              <span className="cs-chipart" aria-hidden dangerouslySetInnerHTML={{ __html: CHIP_SVG(c) }} />
              <span className="cs-chipnum">{c >= 1000 ? `${c / 1000}k` : c}</span>
            </button>
          ))}
        </div>
        <div className="cs-spinrow">
          <button type="button" className={`cs-auto ${auto ? 'on' : ''}`} disabled={!walletReady} {...press(toggleAuto)}>
            <b>{auto ? 'STOP' : 'AUTO'}</b><i className="cs-num">{auto ? `${auto} LEFT` : `×${AUTO_SPINS}`}</i>
          </button>
          {/* SPIN stays tappable before the balance is known: `doSpin` refuses it with a plain
              sentence, which tells the player more than a dead button ever would. */}
          <button type="button" className={`cs-spin ${busy && !auto ? 'busy' : ''} ${auto ? 'stop' : ''} ${!busy && !auto && hint && walletReady ? 'hurry' : ''}`} {...press(onSpinPress)}>
            <b>{auto ? 'STOP AUTO' : busy ? 'SPINNING' : 'SPIN'}</b><i className="cs-num">{auto ? `${inr(chip)} × ${auto}` : inr(chip)}</i>
          </button>
        </div>
        </div>
      </div>

      {/* THE FOOT — the owner's call: the last twelve results earn this strip, the tally text did
          not. Newest sits on the right, where the eye lands after a spin. */}
      <div className="cs-foot">
        {/* `hist` is stored NEWEST FIRST, so the run reads left to right newest to oldest and the
            newest is index 0 — which is also where the `new` flash belongs. It used to sit on
            `a.length - 1`, i.e. the OLDEST chip, so the wrong one flashed after every spin. */}
        <button type="button" className="cs-histbtn" {...press(() => setHistOpen(true))}>MY HISTORY</button>
        <div className="cs-runs">
          {hist.slice(0, 7).map((h, i) => { const d = dotOf(h);
            return <span key={h.id} className={`cs-dot cs-num cs-dot--${d.cls} ${i === 0 ? 'new' : ''}`}>{d.text}</span>; })}
          {hist.length === 0 && <span className="cs-dot cs-dot--none">no spins yet</span>}
        </div>
        <button type="button" className="cs-pill" {...press(() => setRulesOpen(true))}>RULES</button>
      </div>

      {burst && <div className={`cs-burst ${burst}`} aria-hidden />}
      {coins.length > 0 && (
        <div className="cs-coins" aria-hidden>
          {coins.map((b) => Array.from({ length: b.n ?? 8 }, (_, i) => (
            <span key={`${b.id}-${i}`} className="cs-coin" style={{ ['--cs-cx' as string]: `${b.cx}px`, ['--cs-cy' as string]: `${b.cy}px`, ['--cs-d' as string]: `${(i * Math.min(45, 700 / (b.n ?? 8))).toFixed(0)}ms`, ['--cs-jit' as string]: `${((i % 5) - 2) * 11}px` }} />
          )))}
        </div>
      )}
      {hint && !busy && walletReady && <div className="cs-hint">👇 PICK A CHIP · TAP SPIN</div>}
      {rulesOpen && (
        <>
          <div className="cs-backdrop" {...press(() => setRulesOpen(false))} />
          <div className="cs-sheet">
            <i />
            <h3>CASH CASINO — PAYTABLE &amp; RULES</h3>
            <div>
              1. Pick a chip — that is your stake for one spin.<br />2. Tap SPIN. Three reels stop left to right.<br />3. Five paylines are always on: top row, middle row, bottom row and both diagonals.
              <b>WHAT PAYS (TOTAL RETURN ON YOUR WHOLE STAKE)</b>7 7 7 on a line → X25. Three bells → X20. Three diamonds → X15. Three beers → X10. Three cherries → X5. Any two 7s on a line → X2. Three keno balls anywhere on the screen → X8. Wins on different lines add up (a full screen of 7s is 5 lines × X25).
              <b>WHAT YOU GET BACK</b>₹100 on a X5 win → ₹500 back. No matching line → your stake is gone. The balance changes the moment the reels stop.
              <b>AUTO</b>AUTO plays 10 spins at your chip, one after another. Tap STOP any time; the spin already running finishes.
              <b>FAIR SPIN</b>Each reel has 32 stops. Every spin is a fresh random stop on each reel — the picture you see is exactly what was drawn.
            </div>
            <div className="cs-hist">
              <em>RECENT</em>
              {hist.slice().reverse().map((h) => { const d = dotOf(h); return <span key={h.id} className={`cs-dot cs-num cs-dot--${d.cls}`}>{d.text}</span>; })}
              {hist.length === 0 && <span className="cs-dot cs-dot--none">no spins yet</span>}
            </div>
            <button type="button" {...press(() => setRulesOpen(false))}>GOT IT</button>
          </div>
        </>
      )}

      {/* MY HISTORY — the session, not just the run of chips in the foot. The strip answers "what
          just happened"; this answers "how am I doing", which is the question a player actually has
          when they tap it. Same sheet furniture as the rules modal so it reads as one game. */}
      <CasinoMenu
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        soundOn={soundOn}
        musicOn={musicOn}
        onToggleSound={() => setSoundOn(setCsSoundOn(!isCsSoundOn()))}
        onToggleMusic={() => setMusicOn(setCsMusicOn(!isCsMusicOn()))}
        onRules={() => { setSettingsOpen(false); setRulesOpen(true); }}
        onHistory={() => { setSettingsOpen(false); setHistOpen(true); }}
        onExitGame={() => { setSettingsOpen(false); navigate('/games'); }}
      />

      {histOpen && (() => {
        const staked = hist.reduce((n, h) => n + h.stake, 0);
        const won = hist.reduce((n, h) => n + h.paid, 0);
        const net = won - staked;
        const wins = hist.filter((h) => h.paid > 0).length;
        return (
          <>
            <div className="cs-backdrop" {...press(() => setHistOpen(false))} />
            <div className="cs-sheet">
              <i />
              <h3>MY HISTORY — THIS SESSION</h3>
              <div className="cs-htot">
                <span><em>SPINS</em><b className="cs-num">{hist.length}</b></span>
                <span><em>WINS</em><b className="cs-num">{wins}</b></span>
                <span><em>STAKED</em><b className="cs-num">{inr(staked)}</b></span>
                <span><em>WON</em><b className="cs-num">{inr(won)}</b></span>
                <span className={`net ${net >= 0 ? 'up' : 'down'}`}><em>NET</em>
                  <b className="cs-num">{net >= 0 ? '+' : '−'}{inr(Math.abs(net))}</b></span>
              </div>
              {hist.length === 0
                ? <p className="cs-hempty">No spins yet. Pick a chip and tap SPIN — every result lands here.</p>
                : <ol className="cs-hlist">
                    {hist.map((h, i) => { const d = dotOf(h); return (
                      <li key={h.id} className={h.paid > 0 ? 'win' : ''}>
                        <span className="cs-hn cs-num">#{hist.length - i}</span>
                        <span className={`cs-dot cs-num cs-dot--${d.cls}`}>{d.text}</span>
                        <span className="cs-hstake cs-num">{inr(h.stake)}</span>
                        <span className="cs-hpaid cs-num">{h.paid > 0 ? `+${inr(h.paid)}` : '—'}</span>
                      </li>); })}
                  </ol>}
              <button type="button" {...press(() => setHistOpen(false))}>CLOSE</button>
            </div>
          </>
        );
      })()}
    </div>
    </div>
  );
};

export default CsPage;
